import ReactGA from 'react-ga4';
import { analyticsConfig, AnalyticsEvent, ErrorEvent, SearchEvent, PlaylistEvent, ShareEvent } from '../../config/analytics.config';
import { logger } from '../../utils/logger';

export class GoogleAnalyticsService {
  private static instance: GoogleAnalyticsService;
  private initialized = false;
  private sessionId: string;
  private userId?: string;

  private constructor() {
    this.sessionId = this.generateSessionId();
  }

  static getInstance(): GoogleAnalyticsService {
    if (!GoogleAnalyticsService.instance) {
      GoogleAnalyticsService.instance = new GoogleAnalyticsService();
    }
    return GoogleAnalyticsService.instance;
  }

  initialize(): void {
    if (this.initialized || !analyticsConfig.ga4.enabled || !analyticsConfig.ga4.measurementId) {
      logger.log('GA4 initialization skipped:', {
        initialized: this.initialized,
        enabled: analyticsConfig.ga4.enabled,
        measurementId: analyticsConfig.ga4.measurementId
      });
      return;
    }

    try {
      logger.log('Initializing GA4 with ID:', analyticsConfig.ga4.measurementId);
      ReactGA.initialize(analyticsConfig.ga4.measurementId, {
        gaOptions: {
          anonymizeIp: analyticsConfig.privacy.anonymizeIp,
          cookieFlags: analyticsConfig.privacy.cookieFlags,
        },
        gtagOptions: {
          debug_mode: analyticsConfig.ga4.debug,
          session_id: this.sessionId
        }
      });

      this.initialized = true;
      logger.log('GA4 initialized successfully');
      
      this.trackEvent({
        name: analyticsConfig.events.SESSION_START,
        parameters: {
          session_id: this.sessionId,
          timestamp: Date.now()
        }
      });
    } catch (error) {
      logger.error('Failed to initialize Google Analytics:', error);
    }
  }

  // Set user ID (anonymized)
  setUserId(userId: string): void {
    this.userId = userId;
    if (this.initialized) {
      ReactGA.set({ user_id: userId });
    }
  }

  // Track page view
  trackPageView(path: string, title?: string): void {
    if (!this.initialized) return;
    
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title
    });
  }

  // Generic event tracking
  trackEvent(event: AnalyticsEvent): void {
    if (!this.initialized) {
      logger.log('GA4 not initialized, skipping event:', event.name);
      return;
    }

    const eventData = {
      category: event.name.split('_')[0], // Extract category from event name
      action: event.name,
      label: event.parameters?.label,
      value: event.parameters?.value,
      nonInteraction: event.parameters?.nonInteraction,
      transport: 'beacon' as const
    };
    
    logger.log('GA4 tracking event:', eventData);
    ReactGA.event(eventData);
  }

  // Track search events
  trackSearch(searchEvent: SearchEvent): void {
    this.trackEvent({
      name: analyticsConfig.events.SEARCH_ARTIST,
      parameters: {
        search_term: searchEvent.query,
        artist_name: searchEvent.artist,
        results_count: searchEvent.resultsCount,
        search_type: searchEvent.searchType,
        time_to_results: searchEvent.timeToResults
      }
    });
  }

  // Track playlist events
  trackPlaylist(playlistEvent: PlaylistEvent): void {
    const eventName = {
      created: analyticsConfig.events.PLAYLIST_CREATED,
      saved: analyticsConfig.events.PLAYLIST_SAVED,
      copied: analyticsConfig.events.PLAYLIST_COPIED,
      played: analyticsConfig.events.PLAYLIST_PLAYED
    }[playlistEvent.action];

    this.trackEvent({
      name: eventName,
      parameters: {
        artist_name: playlistEvent.artistName,
        artist_id: playlistEvent.artistId,
        song_count: playlistEvent.songCount,
        platform: playlistEvent.platform,
        playlist_id: playlistEvent.playlistId
      }
    });
  }

  // Track share events
  trackShare(shareEvent: ShareEvent): void {
    this.trackEvent({
      name: analyticsConfig.events.SHARE_OPENED,
      parameters: {
        method: shareEvent.method,
        artist_name: shareEvent.artistName,
        playlist_id: shareEvent.playlistId,
        success: shareEvent.success
      }
    });

    // Track specific share method
    if (shareEvent.method === 'instagram') {
      this.trackEvent({
        name: analyticsConfig.events.SHARE_INSTAGRAM,
        parameters: {
          artist_name: shareEvent.artistName,
          success: shareEvent.success
        }
      });
    }
  }

  // Track errors
  trackError(errorEvent: ErrorEvent): void {
    this.trackEvent({
      name: analyticsConfig.events.ERROR_OCCURRED,
      parameters: {
        error_message: errorEvent.error.message,
        error_stack: errorEvent.error.stack,
        severity: errorEvent.severity,
        context: JSON.stringify(errorEvent.context || {}),
        timestamp: Date.now()
      }
    });
  }

  // Track timing
  trackTiming(category: string, variable: string, value: number, label?: string): void {
    if (!this.initialized) return;

    ReactGA.gtag('event', 'timing_complete', {
      event_category: category,
      name: variable,
      value: value,
      event_label: label
    });
  }

  // Generate session ID
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get current session ID
  getSessionId(): string {
    return this.sessionId;
  }
}

// Export singleton instance
export const googleAnalytics = GoogleAnalyticsService.getInstance();